import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroIds } from '@/mocks/works';
import { getWorkById, typeLabel } from '@/utils/works';

export default function HeroCarousel() {
  const slides = heroIds.map((id) => getWorkById(id)).filter((w): w is NonNullable<typeof w> => Boolean(w));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const goPrev = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setIndex((prev) => (prev + 1) % slides.length);
  const current = slides[index];

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-primary-950">
      {slides.map((work, i) => (
        <div
          key={work.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={work.backdrop}
            alt={`${work.title} 剧照`}
            title={`${work.title} ${typeLabel(work.type)}主推剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/60 to-secondary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/80 via-transparent to-secondary-950/30"></div>
        </div>
      ))}

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div key={current.id} className="w-full max-w-2xl animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-secondary-950">
              {typeLabel(current.type)}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 backdrop-blur border border-background-50/20">
              编辑主推
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {current.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-background-200">
            <span className="flex items-center gap-1 text-accent-400 font-medium">
              <i className="ri-star-fill" aria-hidden="true"></i>
              {current.rating.toFixed(1)} 分
            </span>
            <span>{current.year}</span>
            <span>{current.region}</span>
            <span>{current.genres.join(' / ')}</span>
            {current.episodes > 0 && <span>全 {current.episodes} 集</span>}
          </div>
          <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-3 max-w-xl">
            {current.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-background-50/10 text-background-50 text-sm font-medium border border-background-50/25 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{typeLabel(current.type)}
            </Link>
          </div>
        </div>
      </div>

      {/* Controls */}
      <button
        type="button"
        onClick={goPrev}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/15 text-background-50 border border-background-50/25 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="上一部"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={goNext}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/15 text-background-50 border border-background-50/25 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="下一部"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((work, i) => (
          <button
            key={work.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-7 bg-accent-500' : 'w-2.5 bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到 ${work.title}`}
          />
        ))}
      </div>
    </section>
  );
}